import React, { useState, useEffect, useRef } from "react";
import { Send, User, Bot, ShieldCheck } from "lucide-react";
import { logInteraction, getKB } from "../../lib/api";
import { orchestrateAI } from "../../lib/gemini";
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence } from "framer-motion";

export function WhatsAppSimulator() {
  const [messages, setMessages] = useState<any[]>([
    {
      id: "initial",
      text: "Welcome to MyBank Limited! 🏦 Please select your preferred language / Fadlan dooro luqadaada:",
      sender: "bot",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("en");
  const [sessionId] = useState(uuidv4());
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg = { id: uuidv4(), text: input, sender: "user", timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    const currentInput = input;
    setInput("");
    setLoading(true);

    try {
      // 1. Get KB from backend
      const kb = await getKB();

      // 2. Call AI from frontend
      const responseText = await orchestrateAI(currentInput, language, kb);

      const botMsg = { id: uuidv4(), text: responseText, sender: "bot", timestamp: new Date() };
      setMessages((prev) => [...prev, botMsg]);

      // 3. Log interaction to backend
      await logInteraction({
        sessionId,
        userMessage: currentInput,
        botResponse: responseText,
        intent: responseText.includes("HANDOFF") ? "Escalation" : "Info",
        language
      });
    } catch (error) {
      console.error(error);
      const errorMsg = {
        id: uuidv4(),
        text: "⚠️ Connection error. Please ensure your Gemini API key is set.",
        sender: "bot",
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[600px] w-full max-w-md mx-auto bg-[#efeae2] rounded-xl shadow-2xl overflow-hidden border border-gray-300">
      {/* Header */}
      <div className="bg-[#001F5F] p-3 px-4 flex items-center gap-3 text-white shadow-md z-10">
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden border border-white/10">
          <Bot className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[16px] leading-tight">MyBank Assistant</h3>
          <p className="text-[12px] opacity-90">Online</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="text-white border border-white/40 bg-white/10 hover:bg-white/20 cursor-pointer text-[11px] px-2 py-1 rounded-full transition-colors"
            onClick={() => setLanguage(language === "en" ? "so" : "en")}
          >
            {language === "en" ? "English" : "Somali"}
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 p-4 overflow-y-auto relative bg-[#efeae2]"
        ref={scrollRef}
        style={{
          backgroundImage: `url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")`,
          backgroundBlendMode: "overlay",
          backgroundSize: "400px"
        }}
      >
        <div className="flex flex-col gap-4 min-h-full">
          <div className="bg-[#fff5c4] p-2 px-4 rounded-lg self-center text-[11px] text-[#54656f] shadow-sm flex items-center gap-2 border border-[#e1d9b7] z-10">
            <ShieldCheck size={12} className="text-[#54656f]" /> Messages are secured with banking-grade encryption
          </div>

          <AnimatePresence>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, scale: 0.95, y: 5 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`max-w-[85%] p-2 px-3 rounded-lg shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] relative z-10 ${msg.sender === "user"
                  ? "bg-[#e0efff] self-end rounded-tr-none"
                  : "bg-white self-start rounded-tl-none"
                  }`}
              >
                <p className="text-[14.2px] leading-relaxed text-[#111b21] whitespace-pre-wrap">{msg.text}</p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  <p className="text-[11px] text-[#667781]">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {msg.sender === "user" && (
                    <div className="flex">
                      <svg viewBox="0 0 16 11" width="16" height="11" fill="#53bdeb"><path d="M11.01 1.492a.5.5 0 0 0-.707.707l3.314 3.314-3.314 3.314a.5.5 0 1 0 .707.707l3.668-3.668a.5.5 0 0 0 0-.707l-3.668-3.668zM4.51 1.492a.5.5 0 0 0-.707.707l3.314 3.314-3.314 3.314a.5.5 0 1 0 .707.707l3.668-3.668a.5.5 0 0 0 0-.707L4.51 1.492z"></path></svg>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <div className="bg-white self-start p-3 rounded-lg rounded-tl-none shadow-sm flex gap-1 relative z-10">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
            </div>
          )}

          <div className="h-2" />
        </div>
      </div>

      {/* Input Area */}
      <div className="p-2 px-3 bg-[#f0f2f5] flex gap-2 items-center">
        <div className="flex-1 bg-white rounded-full px-4 py-1.5 flex items-center shadow-sm border border-gray-200">
          <input
            placeholder="Type a message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            className="w-full bg-transparent border-none focus:outline-none h-8 text-[15px] p-0 placeholder:text-gray-400"
          />
        </div>
        <button
          onClick={handleSend}
          className="bg-[#017EFF] hover:bg-[#0069D9] text-white rounded-full shrink-0 w-11 h-11 shadow-md flex items-center justify-center transition-colors"
        >
          <Send size={20} className="ml-0.5" />
        </button>
      </div>
    </div>
  );
}
